import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ImagePlus, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { avatarSrc } from "@/lib/api";
import { useLang } from "@/lib/i18n";

export const FORUM_CATEGORIES = ["colecciones", "series", "comics", "videojuegos", "eventos", "general"];

export const Avatar = ({ author, size = 36 }) => {
  const name = author?.name || "?";
  return author?.picture ? (
    <img src={avatarSrc(author.picture)} alt="" width={size} height={size} className="shrink-0 rounded-full object-cover" style={{ width: size, height: size }} referrerPolicy="no-referrer" />
  ) : (
    <span className="grid shrink-0 place-items-center rounded-full bg-[var(--green)] font-display text-[#0d0c0a]" style={{ width: size, height: size, fontSize: size * 0.5 }}>{name[0].toUpperCase()}</span>
  );
};

export const useTimeAgo = () => {
  const { lang } = useLang();
  const rtf = useMemo(() => new Intl.RelativeTimeFormat(lang, { numeric: "auto" }), [lang]);
  return (iso) => {
    const s = (new Date(iso).getTime() - Date.now()) / 1000;
    const steps = [[60, "second"], [3600, "minute"], [86400, "hour"], [604800, "day"], [2629800, "week"], [31557600, "month"], [Infinity, "year"]];
    const div = { second: 1, minute: 60, hour: 3600, day: 86400, week: 604800, month: 2629800, year: 31557600 };
    const [, unit] = steps.find(([lim]) => Math.abs(s) < lim);
    return rtf.format(Math.round(s / div[unit]), unit);
  };
};

/** Wraps children in a link to the author's public profile (if the author has a handle). */
export const AuthorLink = ({ author, className = "shrink-0", testid, children }) =>
  author?.handle ? <Link to={`/u/${author.handle}`} className={className} data-testid={testid}>{children}</Link> : <span className={className}>{children}</span>;

/** Shown instead of a composer when the visitor is not signed in. */
export const JoinPrompt = ({ next }) => {
  const { t } = useLang();
  return (
    <div className="rounded-2xl border border-dashed border-white/20 bg-[var(--card)] px-5 py-6 text-sm" data-testid="community-join-prompt">
      <p className="text-[var(--muted)]">{t("com.joinToPost")}</p>
      <Link to={`/entrar?next=${encodeURIComponent(next)}`} className="btn-primary mt-4" data-testid="community-join-btn">{t("auth.login")}</Link>
    </div>
  );
};

export const useIsMine = () => {
  const { member } = useAuth();
  return (doc) => !!member && doc.author_id === member.member_id;
};

export const PhotoPicker = ({ file, setFile, testid }) => {
  const { t } = useLang();
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => {
    return () => { if (preview) URL.revokeObjectURL(preview); };
  }, [preview]);
  if (preview) {
    return (
      <div className="relative inline-block">
        <img src={preview} alt="" className="h-20 w-20 rounded-lg object-cover" />
        <button type="button" onClick={() => setFile(null)} className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-[#efe8dc] text-[#0d0c0a]" aria-label={t("com.removePhoto")} data-testid={`${testid}-remove`}><X size={13} /></button>
      </div>
    );
  }
  return (
    <label className="btn-ghost !py-2 cursor-pointer" data-testid={testid}>
      <ImagePlus size={15} /> {t("com.addPhoto")}
      <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
    </label>
  );
};

export const PendingNote = ({ show }) => {
  const { t } = useLang();
  return show ? <p className="mt-3 text-sm text-[var(--green)]" data-testid="community-pending-note">{t("com.pending")}</p> : null;
};
